import {Component, OnInit, inject, DestroyRef} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Instance } from 'src/app/models/instance/instance';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import {translate} from '@jsverse/transloco';
import { Solution } from 'src/app/models/service/solution';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { RouterSegments } from 'src/app/models/router-segments';
import { Permission } from 'src/app/models/principal/permission';
import {MatDialog} from '@angular/material/dialog';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ComponentService } from 'src/app/services/api/component.service';
import {InstallationSpecificationService} from 'src/app/services/util/installation-specification.service';
import { ComponentType } from 'src/app/models/service/component-type';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';

const REQUIRED_PERMISSIONS = [
  Permission.CREATE_SANDBOX,
  Permission.CREATE_SERVICE_DEFINITION,
  Permission.VIEW_ENTERPRISE,
];

@Component({
    selector: 'app-update-resource-configuration',
    templateUrl: './update-resource-configuration.component.html',
    styleUrls: ['./update-resource-configuration.component.scss'],
    standalone: false
})
export class UpdateResourceConfigurationComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);
  service: Solution;
  blueprint: Blueprint;
  instances: Instance[];
  RouterSegments = RouterSegments;
  tenant: string;
  breadcrumbItems: BreadcrumbItem[];
  permissionsMap: { [key: string]: boolean };
  ComponentType = ComponentType;
  serviceId: any;
  instance: any;
  instanceId: string;
  errorMessage: any;
  solutionVersion: any;
  isLoading = true;
  tbcComponents = [];
  exchangeComponents = [];
  tbc = ComponentType.TBC;
  exchange = ComponentType.EXCHANGE;
  review: any = 'review';
  activePage = ComponentType.TBC;
  nextPage = ComponentType.EXCHANGE;
  installationSpecificationForms: any;
  activeForm = false;
  solution: SolutionVersion;
  version: string;
  allInstances: any;
  sortedSolutionVersionId: any;
  tenantId: string;
  parameters: any;
  isComponentLoaded = false;
  isContinueDisabled = false;

  readonly INSTANCE_AVAILABLE_STATUS = [DeployStatus.DEPLOYED, DeployStatus.UPDATED,
    DeployStatus.UPDATE_FAILED, DeployStatus.TESTED, DeployStatus.TEST_FAILED,
    DeployStatus.CONFIGURED, DeployStatus.CONFIGURATION_FAILED,
    DeployStatus.FAILED, DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, DeployStatus.PRE_FAILBACK_FAILED, DeployStatus.POST_FAILBACK_FAILED, DeployStatus.STOP_FAILED];
  disableSubmit: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private solutionsService: SolutionsService,
    private blueprintsService: BlueprintsService,
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private instancesService: InstancesService,
    private componentService: ComponentService,
    private installationSpecificationService: InstallationSpecificationService,
    private dialog: MatDialog,
    private toastrService: CustomToastrService
  ) {
    
  }

  ngOnInit(): void {
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.tenant = this.authService.principal.provider;
    this.getParameters();
  }

  getParameters(): any {
    this.instanceId = this.activatedRoute.snapshot.params.id;
    this.instancesService.getInstanceParameters(this.instanceId).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      this.parameters = data?.parameters;
      this.loadData();
    }, (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      this.loadData();
    });
  }

  loadData(): void {
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    this.instancesService.getInstanceById(this.instanceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
    (instance) => {
      this.instance = instance;
      if (!this.INSTANCE_AVAILABLE_STATUS.includes(this.instance?.status)) {
        this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , this.instance.tenantId, RouterSegments.BLUEPRINTS, blueprintId])
      }
      this.isLoading = false;
      this.tenantId = instance.tenantId;        
      this.blueprintsService.getBlueprint(blueprintId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(blueprint => {
        this.blueprint = blueprint;
        this.configureBreadcrumb();
      });
      this.solutionsService.getSolutionVersion(this.instance.solutionId, this.instance.solutionVersionId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.solution = data;
        this.version = data?.version;
        if (this.version) {
          this.blueprintsService.getBlueprintVersionById(blueprintId, this.instance.blueprintVersionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (blueprint) => {
            if (blueprint?.capabilities) {
              blueprint.capabilities?.forEach(capability => {
                const version = data?.capabilities.find(item=> item?.name === capability?.name)?.version; 
                if (capability?.componentType === this.exchange) {
                  this.exchangeComponents.push({name: capability?.name, displayName: capability?.displayName, version: version, 'active': false, dataReceived: false});
                } else {
                  const component = {name: capability?.name, displayName: capability?.displayName, version: version, 'active': false, dataReceived: false}
                  this.tbcComponents?.push(component);
                  if (this.tbcComponents?.length && component) {
                    this.getComponent(component, this.tbc, true);
                  }
                }
              })
            }
          })
        }
      });
    });
  }

  getComponent(component, type, initialLoad? : boolean): void {
    if (component) {
      if (type === this.tbc) {
        this.tbcComponents?.forEach(item => item.active = false);
        initialLoad ? this.tbcComponents[0].active = true : component.active = true;
      } else if (type === this.exchange) {
        this.exchangeComponents?.forEach(item => item.active = false);
        if(component) {
          initialLoad ? this.exchangeComponents[0].active = true : component.active = true;
        }
      }    
      if (!component?.dataReceived) {
        this.componentService.getComponentsNameVersion(component.name, component.version).subscribe((result) => {
          this.isComponentLoaded = true;
          if (result?.parameterSpec) {
            component.form = this.installationSpecificationService.buildDynamicForms(
              atob(result.parameterSpec)
            );
            const installDocRaw = this.parameters?.components?.find(item => item.name === component.name)?.parameterDoc;
            if (installDocRaw) {
              this.getPreviousVersionData(installDocRaw, component.form);
            } else {
              component.form.forEach(nestedForm => {
                nestedForm?.property?.properties?.forEach(element => {
                  if (element['x-value-type'] === 'secret') {
                    element.hide = true;
                  }
                });
              });
            }
          } else {
            component.form = component.form;
          }
  
          if (component.active) {
            this.installationSpecificationForms = component.form;
            this.activeForm = true;
            this.isContinueDisabled = true;
          }
        }, () => {
          this.isComponentLoaded = true;
        });
        component.dataReceived = true;
      } else {
        if (component.active) {
          this.installationSpecificationForms = component.form;
          this.activeForm = true;
        }
      }
    }
  }

  goBack(): void {
    const isUpdate = (this.INSTANCE_AVAILABLE_STATUS.includes(this.instance.status) && !this.parameters?.componentParameterStatus);
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: isUpdate ? translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.UPDATE_CANCEL_TITLE') : translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.CANCEL_TITLE'),
        message: isUpdate? translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.UPDATE_CANCEL_MESSAGE') : translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.CANCEL_MESSAGE'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.blueprint?.id]);
        }
      });
  }

  previous(page): void {
    if(page === this.tbc) {
      this.goBack();    
    } else if(page === this.exchange) {
      this.activePage = this.tbc;
      this.nextPage = this.exchange;
      this.tbcComponents[0].dataReceived = false;
      this.getComponent(this.tbcComponents[0], this.tbc);
    } else if(page === this.review) {
      this.activePage = this.exchange;
      this.nextPage = this.review;
      this.tbcComponents[0].dataReceived = false;
      this.getComponent(this.exchangeComponents[0], this.exchange);
    }
  }

  continue(page): void {

    if(page === this.tbc) {
      const invalidTBCComponents = this.tbcComponents
        ?.filter(comp => Array.isArray(comp.form) && !comp.form.every(f => f?.formGroup?.valid))
        .map(comp => comp.displayName || comp.name);
      const isValidTBCForm = invalidTBCComponents?.length === 0;

      if (!isValidTBCForm) {
      this.dialog.open(GenericErrorComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.FORM_VALIDATION_ERROR'),
            validationError: true,
            invalidComponents: invalidTBCComponents
          }
      });
      } else {
       this.activePage = this.exchange;
        this.nextPage = this.review;
        this.exchangeComponents.forEach(exchangeComp => {
          this.getComponent(exchangeComp, this.exchange, true);
        })
    }

    } else if(page === this.exchange) {
        const invalidExchangeComponents = this.exchangeComponents
          ?.filter(comp => Array.isArray(comp.form) && !comp.form.every(f => f?.formGroup?.valid))
          .map(comp => comp.displayName || comp.name);
        const isValidExchangeForm = invalidExchangeComponents?.length === 0;

        if (!isValidExchangeForm) {
        this.dialog.open(GenericErrorComponent, {
            minHeight: '342px',
            width: '566px',
            panelClass: 'flex-dialog',
            data: {
              title:  translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.FORM_VALIDATION_ERROR'),
              validationError: true,
              invalidComponents: invalidExchangeComponents
            }
        });
        } else {
        this.activePage = this.review;
        this.getFilledInstallationDocument();
      }
    } else if(page === this.review) {
      this.activePage = this.review;
      this.onConfigure(event);
    }
  }

  onEdit(page): void {
    if(page === this.tbc) {
      this.activePage = this.tbc;
      this.nextPage = this.exchange;
      this.getComponent(this.tbcComponents[0], this.tbc);
    } else if(page === this.exchange) {
      this.activePage = this.exchange;
      this.nextPage = this.review;
      this.getComponent(this.exchangeComponents[0], this.exchange);
    }
  }

  skip(page): void {
    if(page === this.tbc) {
      this.activePage = this.exchange;
      this.nextPage = this.review;
      this.tbcComponents.forEach((item) => {
        item.active = false;
        item.dataReceived = false;
        item.form = ''
      });
      this.getComponent(this.tbcComponents[0], this.exchange);
    } else if(page === this.exchange) {
      this.exchangeComponents.forEach((item) => {
        item.active = false;
        item.dataReceived = false;
        item.form = ''
      });
      this.getComponent(this.exchangeComponents[0], this.exchange);
      this.activePage = this.review;
    } else if(page === this.review) {
      this.activePage = this.review;
      this.onConfigure(event);
    }
  }

  onConfigure(event): void {
    const parameters = this.getFilledInstallationDocument();
    event.stopPropagation();
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.TITLE'),
        message: translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.SUBTITLE'),
        confirmButtonText: translate('DIALOG.SUBMIT')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.instancesService.putInstanceParameters(this.instanceId, parameters)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data) => {
          this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.blueprint?.id]);
        }, (error) => {
          const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
          this.dialog.open(ErrorDialogComponent, {
            minHeight: '342px',
            width: '630px',
            panelClass: 'flex-dialog',
            data: {
              title:  translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.CONFIGURATION_FAILED'),
              description: errorMessage
            }
          });
        });
      }
    });
  }

  private getFilledInstallationDocument(): any {
    let tbcComponents = [];
    let exchangeComponents = [];
    let tbcInstallationDocument = {};
    let exchangeInstallationDocument = {};
    let parameters = {}
    this.tbcComponents?.forEach(configForm => {
      configForm.form?.forEach(form => {
        const installationSpecificationFormValue = this.installationSpecificationService.getFormValue(
          form);
        Object.assign(tbcInstallationDocument, installationSpecificationFormValue);
        tbcComponents = [...tbcComponents, {name: configForm.name, parameterDoc: installationSpecificationFormValue}];
      });
    });
    this.exchangeComponents?.forEach(configForm => {
      configForm.form?.forEach(form => {
        const installationSpecificationFormValue = this.installationSpecificationService.getFormValue(
          form);
        Object.assign(exchangeInstallationDocument, installationSpecificationFormValue);
        exchangeComponents = [...exchangeComponents, {name: configForm.name, parameterDoc: installationSpecificationFormValue}];
      });
    });
    const configuredData = [ ...tbcComponents, ...exchangeComponents];
    parameters["components"] = configuredData;
    this.disableSubmit = parameters["components"].length === 0 ? false : true;
    return parameters;
  }

  private configureBreadcrumb(): void {
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION]},
    {label: this.blueprint?.name, url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION, this.blueprint?.id]});
  }

  private getPreviousVersionData(installDocRaw, componentForm): void {
    Object.keys(installDocRaw).forEach(key => {
      const propertyKey = Object.keys(installDocRaw[key]);
      const propertyValue = Object.assign(installDocRaw[key]);
      propertyKey.forEach(controlName => {
        componentForm.forEach(nestedForm => {
          nestedForm?.formGroup?.controls[key]?.get(controlName)?.setValue(
            propertyValue[controlName].length === undefined ? '' : propertyValue[controlName]);
            if (propertyValue[controlName] !== '') {
              nestedForm.formGroup.touched = true; 
            }
          nestedForm?.property?.properties?.forEach(element => {
            if (nestedForm?.formGroup?.controls[key]?.get(element.name)) {
              element.updatedValue = nestedForm.formGroup.controls[key].get(element.name).value;
              if (element['x-value-type'] === 'secret' && element.updatedValue) {
                element.hide = true;
                element.readonly = true;
              } else if (element['x-value-type'] === 'secret') {
                element.hide = true;
              }
            }
          });
        });
      });
    });
  }
}